import { useEffect, useRef } from 'react';

import { AppStateStatus } from 'react-native';

import { useSelector } from 'react-redux';

import { selectWarnOnMobileData } from 'store/connection/selectors.ts';
import { StreamTypes } from 'store/player/actions.types.ts';

import { VideoItem } from 'services/data-manager/types.ts';

import debugLog from 'utils/debugLog.ts';
import useNoInternet from 'utils/hooks/useNoInternet.ts';

import { PlaybackStopParams, PlayerError } from '../types.ts';
import useNoInternetConnection from './useNoInternetConnection';
import usePlayerAudioFocus from './usePlayerAudioFocus';
import usePlayerInBackground from './usePlayerInBackground';

type Params = {
    isLive: boolean;
    isCatchup: boolean;
    isNpvr: boolean;
    isVod: boolean;
    isAirPlayConnected: boolean;
    appState: AppStateStatus;
    visible: boolean;
    readyToPlay: boolean;
    playerError: PlayerError;
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
    playerProgress: number;
    playerPaused: boolean;
    onPlayPausePress(): void;
    handleStopPlayback(params?: PlaybackStopParams): void;
    handleSwitchPlayerSource(
        videoItem?: VideoItem,
        playbackType?: StreamTypes,
    ): void;
    handleRestoreLiveStream(): void;
};

type FiniteStreamPersitenceState = {
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
    playerProgress: number | undefined;
};

const DEFAULT_FINITE_STREAM_PERSISTENCE_STATE = {
    videoItem: undefined,
    playbackType: undefined,
    playerProgress: undefined,
};

function useStreamPersistence({
    isLive,
    isCatchup,
    isNpvr,
    isVod,
    isAirPlayConnected,
    appState,
    visible,
    readyToPlay,
    playerError,
    videoItem,
    playbackType,
    playerProgress,
    playerPaused,
    onPlayPausePress,
    handleStopPlayback,
    handleSwitchPlayerSource,
    handleRestoreLiveStream,
}: Params) {
    const shouldWarnOnMobileData = useSelector(selectWarnOnMobileData);

    const prevShouldWarnOnMobileData = useRef(shouldWarnOnMobileData);

    const { isInternetConnected } = useNoInternet();

    const liveStreamPersisted = useRef(false);
    const finiteStreamPersistenceState = useRef<FiniteStreamPersitenceState>({
        ...DEFAULT_FINITE_STREAM_PERSISTENCE_STATE,
    });
    const restoreTimeout = useRef<NodeJS.Timeout | null>(null);
    const restoreStreamOnInternetBackTimeout = useRef<NodeJS.Timeout | null>(
        null,
    );

    const canRestoreLiveStream = liveStreamPersisted.current;
    const canRestoreFiniteStream =
        finiteStreamPersistenceState.current.videoItem &&
        finiteStreamPersistenceState.current.playbackType &&
        finiteStreamPersistenceState.current.playerProgress != null;

    const persistLiveStream = (reason?: string) => {
        debugLog('Persist live stream', reason);

        handleStopPlayback({ resetPauseState: true });
        handleSwitchPlayerSource();
        liveStreamPersisted.current = true;
    };

    const restoreLiveStream = () => {
        handleRestoreLiveStream();
        liveStreamPersisted.current = false;
    };

    const persistFiniteStream = (reason?: string) => {
        debugLog('Persist finite stream', reason);

        finiteStreamPersistenceState.current.videoItem = videoItem;
        finiteStreamPersistenceState.current.playbackType = playbackType;
        finiteStreamPersistenceState.current.playerProgress = playerProgress;
        handleStopPlayback({ resetPauseState: false });
        handleSwitchPlayerSource();
    };

    const restoreFiniteStream = () => {
        if (restoreTimeout.current) {
            clearTimeout(restoreTimeout.current);
            restoreTimeout.current = null;
        }

        restoreTimeout.current = setTimeout(() => {
            handleSwitchPlayerSource(
                {
                    ...(finiteStreamPersistenceState.current
                        .videoItem as VideoItem),
                    sourceConfig: {
                        startOffsetTimelineReference: 'start',
                        startOffset:
                            finiteStreamPersistenceState.current.playerProgress,
                    },
                },
                finiteStreamPersistenceState.current.playbackType,
            );

            finiteStreamPersistenceState.current = {
                ...DEFAULT_FINITE_STREAM_PERSISTENCE_STATE,
            };
        }, 100);
    };

    const restorePersistedStream = (reason?: string) => {
        if (canRestoreLiveStream) {
            debugLog('Restore live stream', reason);
            restoreLiveStream();
        } else if (canRestoreFiniteStream) {
            debugLog('Restore finite stream', reason);
            restoreFiniteStream();
        } else {
            debugLog('No stream to restore', reason);
        }
    };

    // Handle player stream when app goes to background and back to active.
    usePlayerInBackground({
        appState,
        playerError,
        visible,
        readyToPlay,
        onPersistStream: (reason?: string) => {
            if (isInternetConnected) {
                if (isLive) {
                    persistLiveStream(reason);
                } else if (playbackType) {
                    persistFiniteStream(reason);
                }
            }
        },
        onRestoreStream: (reason?: string) => {
            if (isInternetConnected && !shouldWarnOnMobileData) {
                restorePersistedStream(reason);
            }
        },
    });

    // Handle player stream when app changes its audio focus.
    usePlayerAudioFocus({
        appState,
        visible,
        isAirPlayConnected,
        onPersistStream: (reason?: string) => {
            if (isInternetConnected) {
                if (isLive) {
                    persistLiveStream(reason);
                } else if (playbackType) {
                    if ((isVod || isCatchup || isNpvr) && !playerPaused) {
                        onPlayPausePress();
                    }

                    persistFiniteStream(reason);
                }
            }
        },
        onRestoreStream: (reason?: string) => {
            if (isInternetConnected && !shouldWarnOnMobileData) {
                restorePersistedStream(reason);
            }
        },
    });

    // Handle player stream when app losses and restores internet connection.
    useNoInternetConnection({
        playerVisible: visible,
        onNoInternet: () => {
            if (restoreStreamOnInternetBackTimeout.current) {
                clearTimeout(restoreStreamOnInternetBackTimeout.current);
                restoreStreamOnInternetBackTimeout.current = null;
            }

            if (isLive) {
                persistLiveStream('No internet connection');
            } else if (playbackType) {
                persistFiniteStream('No internet connection');
            }
        },
        onIntertnetBack: () => {
            if (!shouldWarnOnMobileData) {
                if (restoreStreamOnInternetBackTimeout.current) {
                    clearTimeout(restoreStreamOnInternetBackTimeout.current);
                    restoreStreamOnInternetBackTimeout.current = null;
                }
                // Timeout is needed for low-end devices to restore the connection properly after
                // app will define that internet connection is back.
                restoreStreamOnInternetBackTimeout.current = setTimeout(() => {
                    restorePersistedStream('Internet connection back');
                }, 5000);
            }
        },
    });

    useEffect(() => {
        if (visible) {
            if (!prevShouldWarnOnMobileData.current && shouldWarnOnMobileData) {
                if (isLive) {
                    persistLiveStream('shouldWarnOnMobileData');
                } else if (playbackType) {
                    persistFiniteStream('shouldWarnOnMobileData');
                }
            }

            if (prevShouldWarnOnMobileData.current && !shouldWarnOnMobileData) {
                // Timeout is needed for low-end devices to restore the connection properly after
                // app will define that internet connection is back.
                setTimeout(() => {
                    restorePersistedStream('shouldWarnOnMobileData');
                }, 5000);
            }
        }

        prevShouldWarnOnMobileData.current = shouldWarnOnMobileData;
    }, [shouldWarnOnMobileData]);

    useEffect(() => {
        if (!visible) {
            if (restoreTimeout.current) {
                clearTimeout(restoreTimeout.current);
                restoreTimeout.current = null;
            }

            if (restoreStreamOnInternetBackTimeout.current) {
                clearTimeout(restoreStreamOnInternetBackTimeout.current);
                restoreStreamOnInternetBackTimeout.current = null;
            }

            liveStreamPersisted.current = false;
            finiteStreamPersistenceState.current = {
                ...DEFAULT_FINITE_STREAM_PERSISTENCE_STATE,
            };
        }
    }, [visible]);
}

export default useStreamPersistence;
